import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight, ArrowRight, ScanLine, HeartPulse, Bot, Building2,
  KeyRound, Wrench, Handshake, ShieldCheck, Sparkles, MessageSquare, Check,
  CalendarClock, BookOpen, MoveRight, QrCode, FileText, Radar, ReceiptText, Zap,
  ClipboardCheck, Boxes, Hammer, Users, Briefcase,
} from "lucide-react";
import { Reveal, MaskedLines, Overline } from "../components/Reveal";
import Seo from "../components/Seo";
import NotifyForm from "../components/NotifyForm";
import { GoldButton } from "../components/GoldButton";
import PhoneMockup from "../components/PhoneMockup";
import AppScreensGallery from "../components/AppScreensGallery";
import { useContent } from "../lib/content";
import { IMAGES, MARQUEE, AI_ASSISTANTS, CARNET_ITEMS, WHY_CHOOSE, ENGAGEMENTS, PERSONAS } from "../data/content";

const QUICK = [
  { icon: Building2, label: "Propriétaire", to: "/espaces" },
  { icon: KeyRound, label: "Airbnb", to: "/espaces" },
  { icon: Wrench, label: "Technicien", to: "/rejoindre" },
  { icon: Handshake, label: "Apporteur", to: "/rejoindre" },
];

const MICRO = ["Dépannage 7j/7", "Une demande en quelques secondes", "Tout corps d'état de proximité", "Un suivi de l'intervention", "Le propriétaire garde la main"];

const PARCOURS = ["Signaler", "Transmettre", "Analyser", "Intervenir", "Suivre", "Conserver l'historique"];

const AVANT_APRES = [
  ["Plusieurs appels et messages", "Une demande centralisée"],
  ["Recherche longue d'un artisan", "Orientation vers le bon professionnel"],
  ["Informations dispersées", "Photos, vidéos et détails dans un même dossier"],
  ["Déplacements inutiles", "Gestion et validation à distance lorsque cela est possible"],
  ["Pas d'historique structuré", "Chaque intervention peut être conservée"],
  ["Stress et perte de temps", "Suivi clair jusqu'à la clôture"],
];

const PARCOURS_CLIC = [
  "Choisir la prestation", "Décrire le besoin", "Ajouter une photo ou une vidéo",
  "Recevoir une orientation", "Valider l'intervention", "Suivre la résolution",
];

const PRESTATIONS_LIST = [
  "Plomberie", "Fuite d'eau", "Remplacement de robinetterie", "Remplacement de siphon",
  "Débouchage de toilettes", "Débouchage d'évier", "Petites réparations sanitaires",
  "Électricité", "Prises et interrupteurs", "Installation de luminaires", "Diagnostic et mise en sécurité",
  "Serrurerie", "Ouverture et fermeture de portes", "Peinture", "Retouches et remise en état",
  "Plâtrerie et plaquisterie", "Carrelage et joints", "Montage et installation",
  "Remplacement d'équipements", "Rénovation et multiservice", "Débarras de cave",
  "Déménagement", "Entretien du logement", "Intervention spécifique sur devis",
  "Travaux et finitions spécifiques (sur devis)",
];

const STORY = [
  { src: "/images/story-panne.webp", n: "01", t: "La panne survient", d: "Fuite sous l'évier : elle ouvre Speed Détresse et signale en quelques secondes, photo à l'appui." },
  { src: "/images/story-technicien.webp", n: "02", t: "Le technicien vérifié intervient", d: "La plateforme oriente la demande vers le bon professionnel, avec un dossier déjà préparé." },
  { src: "/images/story-note.webp", n: "03", t: "Elle valide, elle note, tout est archivé", d: "Paiement débloqué après validation, intervention notée et conservée dans le Carnet de Santé." },
];

const REFLEXE = [
  { n: "7j/7", t: "Une réponse dès que le besoin arrive." },
  { n: "Un seul fil", t: "Tout est suivi au même endroit, de l'alerte à la résolution." },
  { n: "Zéro oubli", t: "Les bonnes informations arrivent au bon moment." },
  { n: "Terrain d'abord", t: "L'IA prépare et organise ; le professionnel intervient et décide." },
];

const LOGEMENT_ITEMS = [
  "Informations du logement", "Historique complet", "Interventions réalisées", "Photos", "Documents",
  "Équipements", "Opérations d'entretien", "États des lieux", "Échéances de maintenance", "Signalements",
  "Informations utiles aux occupants", "Informations utiles aux professionnels", "Localisation selon les autorisations",
  "QR code permanent du logement", "Carnet de Santé du Logement",
];

const ACTION_STEPS = [
  { icon: MessageSquare, t: "Le locataire signale", d: "Photo + description guidée." },
  { icon: Bot, t: "L'IA qualifie", d: "Premiers gestes et niveau d'urgence." },
  { icon: ShieldCheck, t: "Le propriétaire valide", d: "Décision depuis son application." },
  { icon: Wrench, t: "Le technicien intervient", d: "Mission, matériel et tournée optimisés." },
];

const AI_ICONS = { gael: ShieldCheck, jetheque: Wrench, orion: Sparkles, jarvis: Boxes };

const PUBLICS = [
  { img: "/images/story-panne.webp", t: "Particuliers", d: "La panne à la maison" },
  { img: "/images/story-resto-panne.webp", t: "Restaurants & bars", d: "Même en plein service" },
  { img: "/images/story-hotel.webp", t: "Hôtels", d: "Entretien des chambres" },
  { img: "/images/story-magasin.webp", t: "Commerces & magasins", d: "L'activité ne s'arrête pas" },
  { img: "/images/story-salon.webp", t: "Salons de coiffure", d: "Le bac bouché, réglé" },
  { img: "/images/story-agence-immo.webp", t: "Agences immobilières", d: "Toutes leurs demandes centralisées" },
  { img: "/images/story-gestion-locative.webp", t: "Gestion locative", d: "Des dizaines de biens pilotés" },
  { img: "/images/story-bailleur.webp", t: "Bailleurs & syndics", d: "Un tableau de bord par parc" },
];

const CTA_QUESTIONS = [
  "Vous avez besoin d'un dépannage ponctuel ?",
  "Vous gérez plusieurs logements ?",
  "Vous voulez un Carnet de Santé pour vos biens ?",
  "Vous souhaitez gagner du temps sur les locataires et les interventions ?",
  "Vous voulez souscrire à Speed Détresse ?",
  "Vous êtes technicien ou entreprise partenaire ?",
];

export default function Home() {
  const cms = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const SOLUTION_CARDS = [
    { icon: BookOpen, t: cms("home.solution.card1.title", "Un carnet numérique par logement"), d: cms("home.solution.card1.text", "Historique des interventions, factures, garanties, photos et états des lieux sont centralisés et accessibles en un clic.") },
    { icon: Zap, t: cms("home.solution.card2.title", "Un dépannage plus réactif"), d: cms("home.solution.card2.text", "Une demande urgente est orientée vers un professionnel disponible selon son métier, sa zone et ses disponibilités.") },
    { icon: Bot, t: cms("home.solution.card3.title", "Une IA utile au quotidien"), d: cms("home.solution.card3.text", "Diagnostic photo, rappels de maintenance, devis automatisés et organisation des tournées sont assistés par l'IA.") },
  ];

  const PROCESS_STEPS = [
    { tag: "01 — Demande", t: cms("home.process.step1.title", "Déclarer"), d: cms("home.process.step1.text", "Décrivez la panne ou le besoin, ajoutez une photo et précisez le lieu.") },
    { tag: "02 — Analyse", t: cms("home.process.step2.title", "Orienter"), d: cms("home.process.step2.text", "L'IA analyse le métier, la zone, la disponibilité et les informations utiles.") },
    { tag: "03 — Intervention", t: cms("home.process.step3.title", "Agir"), d: cms("home.process.step3.text", "Le professionnel reçoit les détails, intervient et renseigne son compte-rendu.") },
    { tag: "04 — Historique", t: cms("home.process.step4.title", "Conserver"), d: cms("home.process.step4.text", "Photos, documents, coûts et actions réalisées restent associés au logement.") },
  ];

  const ACTEURS = [
    { icon: Building2, t: cms("home.acteurs.card1.title", "Propriétaires et bailleurs"), d: cms("home.acteurs.card1.text", "Gérez plusieurs logements, suivez les dépenses, partagez les accès et anticipez les maintenances.") },
    { icon: Users, t: cms("home.acteurs.card2.title", "Locataires et particuliers"), d: cms("home.acteurs.card2.text", "Obtenez une aide rapide, suivez l'arrivée du technicien et retrouvez vos interventions sans paperasse.") },
    { icon: Wrench, t: cms("home.acteurs.card3.title", "Techniciens"), d: cms("home.acteurs.card3.text", "Recevez des missions ciblées, réduisez les déplacements et utilisez des outils de diagnostic, de devis et de compte-rendu.") },
    { icon: Hammer, t: cms("home.acteurs.card4.title", "Artisans"), d: cms("home.acteurs.card4.text", "Développez votre visibilité auprès de clients qualifiés pour vos travaux de rénovation et d'installation.") },
    { icon: Briefcase, t: cms("home.acteurs.card5.title", "Agences immobilières"), d: cms("home.acteurs.card5.text", "Centralisez les interventions, les états des lieux, les factures et le suivi des coûts de maintenance.") },
    { icon: ShieldCheck, t: cms("home.acteurs.card6.title", "Une gestion plus sereine"), d: cms("home.acteurs.card6.text", "Un seul espace pour coordonner les personnes, les logements, les documents et les paiements.") },
  ];

  const DIFF_ROWS = [
    [cms("home.diff.row1.problem", "Je ne trouve pas d'artisan disponible."), cms("home.diff.row1.answer", "Une orientation selon le métier, la zone et la disponibilité.")],
    [cms("home.diff.row2.problem", "Je ne sais pas ce qui a été fait sur le logement."), cms("home.diff.row2.answer", "Un historique complet avec photos, garanties et documents.")],
    [cms("home.diff.row3.problem", "Je passe mon temps au téléphone."), cms("home.diff.row3.answer", "Les demandes, échanges et informations sont centralisés.")],
    [cms("home.diff.row4.problem", "J'oublie les opérations d'entretien."), cms("home.diff.row4.answer", "Des rappels de maintenance avant qu'un problème ne survienne.")],
    [cms("home.diff.row5.problem", "Je ne sais pas combien cela va coûter."), cms("home.diff.row5.answer", "Des informations tarifaires plus transparentes avant validation.")],
  ];

  return (
    <div data-testid="home-page">
      <Seo path="/" />
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[82vh] items-center overflow-hidden pb-16">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={cms("home.hero.image", IMAGES.hero)} alt="Architecture" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-navy/85" />
          <div className="hero-overlay-grad absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/40" />
          <div className="absolute inset-0 gold-radial" />
          <div className="hero-overlay-grad absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-navy to-transparent" />
        </motion.div>

        <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 pt-28 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
          <motion.div style={{ y: contentY, opacity: fade }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }} className="flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-gold/50 bg-gold/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-testid="hero-badge">
              Speed Détresse IA 2030 • Dépannage 7j/7
            </span>
            <Overline>{cms("home.hero.overline", "La plateforme qui soulage les propriétaires d'Amiens et de la Picardie")}</Overline>
          </motion.div>

          <h1 className="mt-6 font-serif text-5xl leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-8xl">
            <MaskedLines
              lines={cms("home.hero.title", "Le dépannage en un clic.\nTout simplement.").split("\n").filter((l) => l.trim())}
              lineClassName=""
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-slate-300"
          >
            {cms("home.hero.subtitle", "Une fuite, une panne, un débouchage, un problème de serrure, un petit travail ? Demandez une intervention rapidement, 7 jours sur 7, auprès d'un réseau de professionnels. Fini les recherches d'artisan, les appels multipliés et les déplacements pour chaque incident.")}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-6 font-serif text-xl italic text-gold md:text-2xl"
            data-testid="hero-phrase-forte"
          >
            Vous signalez. L'intelligence organise. Le professionnel intervient.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <GoldButton to="/contact" size="lg" data-testid="hero-intervention-btn">
              Demander une intervention <ArrowRight className="h-4 w-4" />
            </GoldButton>
            <GoldButton to="/depannage" size="lg" variant="outline" data-testid="hero-prestations-btn">
              Voir les prestations
            </GoldButton>
            <GoldButton to="/tarifs" size="lg" variant="outline" data-testid="hero-speede13-btn">
              Découvrir Speed Détresse
            </GoldButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-2.5"
            data-testid="hero-micro-messages"
          >
            {MICRO.map((m) => (
              <span key={m} className="flex items-center gap-2 rounded-full border border-white/15 bg-navy-surface/40 px-3.5 py-1.5 text-xs text-slate-200 backdrop-blur-sm">
                <Check className="h-3 w-3 text-gold" strokeWidth={2.5} /> {m}
              </span>
            ))}
          </motion.div>
          </motion.div>
          <div className="flex flex-col items-center gap-8 lg:items-end">
            <PhoneMockup image="/app-screens/espace-proprietaire.webp" alt="Application Speed Détresse — espace propriétaire" />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="w-full max-w-sm rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur-md"
              data-testid="hero-stat-card"
            >
              <strong className="block font-serif text-4xl text-gold">{cms("home.hero.stat1.value", "2 h")}</strong>
              <span className="mt-1.5 block text-sm leading-relaxed text-slate-200">{cms("home.hero.stat1.text", "Un objectif d'intervention rapide pour les urgences, y compris la nuit et le week-end.")}</span>
              <hr className="my-5 border-white/15" />
              <strong className="block font-serif text-4xl text-gold">{cms("home.hero.stat2.value", "1 espace")}</strong>
              <span className="mt-1.5 block text-sm leading-relaxed text-slate-200">{cms("home.hero.stat2.text", "Pour centraliser les logements, les interventions, les documents et les échanges.")}</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-6">
        <Marquee autoFill speed={40} gradient gradientColor="#060B19" gradientWidth={120}>
          {MARQUEE.map((m, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 font-serif text-2xl text-slate-300 md:text-3xl">
              {m} <span className="text-gold">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* PARCOURS DE RÉFÉRENCE */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pt-16 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-center justify-center gap-2" data-testid="home-parcours">
            {PARCOURS.map((step, i) => (
              <span key={step} className="flex items-center gap-2">
                <span className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider ${i === PARCOURS.length - 1 ? "bg-gold text-navy" : "border border-gold/40 text-gold"}`}>
                  {step}
                </span>
                {i < PARCOURS.length - 1 && <MoveRight className="h-4 w-4 text-slate-500" />}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* LA SOLUTION */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>{cms("home.solution.overline", "La solution")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              {cms("home.solution.title", "Tout ce dont votre habitat a besoin, au même endroit.")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              {cms("home.solution.text", "Une plateforme conçue pour réduire les délais, simplifier la coordination et garder une trace fiable de chaque intervention.")}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3" data-testid="home-solution">
            {SOLUTION_CARDS.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <div className="group h-full rounded-2xl border border-white/10 bg-navy p-8 transition-colors hover:border-gold/40">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                    <c.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-white">{c.t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HISTOIRE EN IMAGES */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal><Overline>Le dépannage Speed Détresse, en vrai</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
            De la panne au problème réglé.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3" data-testid="home-story">
          {STORY.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <figure className="group overflow-hidden rounded-3xl border border-white/10 bg-navy-surface/60">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={s.src}
                    alt={s.t}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1 font-serif text-sm font-semibold text-navy">{s.n}</span>
                </div>
                <figcaption className="p-6">
                  <h3 className="font-serif text-xl text-white">{s.t}</h3>
                  <p className="mt-2 text-sm text-slate-400">{s.d}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* POURQUOI SPEED DÉTRESSE */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>Pourquoi Speed Détresse ?</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              Le propriétaire ne devrait plus passer ses journées à gérer des appels de dépannage.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              Une fuite ou une panne urgente entraîne des appels, des recherches d'artisans, des rendez-vous à organiser et des déplacements inutiles. Speed Détresse simplifie cette chaîne.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-12 overflow-hidden rounded-3xl border border-white/10">
            <div className="grid md:grid-cols-[1fr_1fr_1fr]" data-testid="home-avant-apres">
              <div className="hidden border-b border-white/10 bg-navy-surface/60 p-5 md:block" />
              <div className="border-b border-white/10 bg-navy-surface/60 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Avant Speed Détresse</p>
              </div>
              <div className="border-b border-gold/25 bg-navy-elevated p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-gold">Avec Speed Détresse</p>
              </div>
              {AVANT_APRES.map(([avant, avec], i) => (
                <div key={avant} className="contents">
                  <div className={`p-5 text-sm font-medium text-slate-400 ${i % 2 ? "bg-navy-surface/40" : "bg-navy"}`}>{avant}</div>
                  <div className={`p-5 text-sm text-slate-500 line-through decoration-slate-600 ${i % 2 ? "bg-navy-surface/40" : "bg-navy"} md:hidden`}>{avant}</div>
                  <div className={`flex items-center gap-3 p-5 text-sm text-white ${i % 2 ? "bg-navy-elevated/60" : "bg-navy-elevated/40"}`}>
                    <Check className="h-4 w-4 flex-shrink-0 text-gold" strokeWidth={2.5} /> {avec}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-8 font-serif text-2xl italic text-gold">Speed Détresse transforme un problème imprévu en intervention organisée.</p>
          </Reveal>
        </div>
      </section>

      {/* DÉPANNAGE EN UN CLIC */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal><Overline>Dépannage en un clic</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
            Un problème dans le logement ? Un clic pour commencer.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            Sélectionnez la prestation, décrivez le problème, joignez les éléments utiles. Le parcours est simple, même sans connaître le nom de la panne ou de la pièce concernée.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-parcours-clic">
          {PARCOURS_CLIC.map((step, i) => (
            <Reveal key={step} delay={i * 0.05} className="flex items-center gap-4 bg-navy p-6">
              <span className="font-serif text-4xl text-gold/40">{i + 1}</span>
              <p className="text-sm font-medium text-slate-200">{step}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-8 rounded-2xl border border-gold/25 bg-navy-elevated p-7">
            <p className="text-xs uppercase tracking-[0.2em] text-gold">Exemple concret</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Un robinet fuit dans la cuisine. Le client ouvre Speed Détresse, sélectionne « Plomberie », ajoute une photo du robinet et décrit la fuite.
              La demande est organisée, le propriétaire est informé, et un professionnel adapté est recherché selon la zone, la spécialité et les disponibilités.
              Le client reçoit les informations utiles avant de valider — et l'intervention est suivie jusqu'à sa clôture.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="mt-12 text-sm uppercase tracking-[0.2em] text-gold">Tout corps d'état de proximité</p>
          <div className="mt-4 flex flex-wrap gap-2.5" data-testid="home-prestations-chips">
            {PRESTATIONS_LIST.map((p) => (
              <span key={p} className="rounded-full border border-white/10 bg-navy-surface/60 px-3.5 py-1.5 text-xs text-slate-300">{p}</span>
            ))}
          </div>
          <p className="mt-5 text-xs text-slate-500">
            Les travaux spécialisés ou réglementés sont confiés à des professionnels disposant des qualifications, assurances et autorisations nécessaires.
          </p>
        </Reveal>
      </section>

      {/* UNE EXPÉRIENCE FLUIDE — 4 ÉTAPES */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>{cms("home.process.overline", "Une expérience fluide")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              {cms("home.process.title", "De la demande au suivi, chaque étape est claire.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-process">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.tag} delay={i * 0.08}>
                <div className="h-full rounded-xl border border-white/10 border-t-[3px] border-t-gold bg-navy p-7">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold">{s.tag}</p>
                  <h3 className="mt-3 font-serif text-2xl text-white">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* L'APPLICATION EN GRAND */}
      <section className="relative z-10 border-t border-white/10 mx-auto max-w-7xl px-5 pt-24 lg:px-8">
        <div className="text-center">
          <Reveal><Overline>{cms("home.app.overline", "Le cœur de Speed Détresse")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mx-auto mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl lg:text-6xl">
              {cms("home.app.title", "Tout se passe dans l'application.")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
              {cms("home.app.text", "Le site vous présente Speed Détresse. L'application, elle, fait le travail : signaler, suivre, décider, archiver — toute la vie de votre logement, dans votre poche.")}
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-app-features">
          {[
            { icon: Zap, t: "Signaler une panne en 30 secondes", d: "Photo, description guidée, niveau d'urgence : votre demande part immédiatement, sans appel ni attente." },
            { icon: Radar, t: "Suivre l'intervention en direct", d: "Chaque étape est notifiée : qualification, départ du technicien, intervention, compte-rendu." },
            { icon: FileText, t: "Tout retrouver, tout de suite", d: "Documents, photos, rapports et historique complet du logement, centralisés et conservés." },
            { icon: ScanLine, t: "États des lieux numériques", d: "Photos reliées à chaque élément, rapport signé et comparé automatiquement au précédent." },
            { icon: QrCode, t: "Le QR code de votre logement", d: "Un accès instantané aux bonnes informations, pour chaque occupant et chaque professionnel." },
            { icon: ReceiptText, t: "Des prix connus à l'avance", d: "Consultez le tarif de la prestation avant de confirmer. Zéro mauvaise surprise." },
          ].map((f, i) => (
            <Reveal key={f.t} delay={(i % 3) * 0.08}>
              <div className="group h-full rounded-2xl border border-white/10 bg-navy-surface/60 p-7 transition-colors hover:border-gold/40">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                  <f.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-serif text-xl text-white">{f.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16">
            <AppScreensGallery />
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-16 grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr]" data-testid="app-video-block">
            <div className="relative mx-auto">
              <div className="absolute -inset-6 rounded-full bg-gold/15 blur-3xl" />
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/app-screens/app-demo-poster.jpg"
                data-testid="app-demo-video"
                className="relative w-[240px] rounded-[2rem] border border-white/15 shadow-2xl shadow-black/60 sm:w-[260px]"
              >
                <source src="/app-screens/app-demo.mp4" type="video/mp4" />
                <source src="/app-screens/app-demo.webm" type="video/webm" />
              </video>
            </div>
            <div>
              <Overline>L'application en mouvement</Overline>
              <h3 className="mt-4 font-serif text-3xl tracking-tight text-white md:text-4xl">
                Un aperçu vivant, directement depuis l'application.
              </h3>
              <p className="mt-4 max-w-lg text-slate-300">
                Espace propriétaire, suivi des interventions, devis, techniciens notés, partenaires, marché immobilier entre abonnés : les vrais écrans, en 20 secondes.
              </p>
              <a
                href="/app-screens/app-demo.mp4"
                download="speed-detresse-application.mp4"
                data-testid="app-demo-download"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 text-sm text-gold transition-colors hover:bg-gold/10"
              >
                Télécharger le clip (MP4) — idéal pour vos réseaux
              </a>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-col items-center gap-6">
            <div className="flex flex-wrap items-center justify-center gap-3" data-testid="app-store-badges">
              <span className="rounded-full border border-gold/40 bg-navy px-5 py-2.5 text-sm text-gold">Bientôt sur iOS</span>
              <span className="rounded-full border border-gold/40 bg-navy px-5 py-2.5 text-sm text-gold">Bientôt sur Android</span>
            </div>
            <GoldButton to="/application" size="lg" data-testid="home-app-cta">
              Explorer l'application <ArrowRight className="h-4 w-4" />
            </GoldButton>
          </div>
        </Reveal>
      </section>

      {/* DEPANNAGE SPOTLIGHT */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pt-24 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-gold/25 bg-navy-elevated p-10 md:p-14" data-testid="home-depannage-spotlight">
            <div className="absolute inset-0 gold-radial" />
            <div className="relative grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <Overline>{cms("home.depannage.overline", "Prestations uniques · sans abonnement")}</Overline>
                <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">
                  {cms("home.depannage.title", "Une urgence ? Speed Détresse prend le relais.")}
                </h2>
                <p className="mt-5 max-w-xl text-lg text-slate-300">
                  {cms("home.depannage.text", "Fuite, panne électrique, chauffage, serrure… décrivez votre besoin, choisissez votre créneau et consultez le prix de la prestation avant de confirmer.")}
                </p>
                <div className="mt-8">
                  <GoldButton to="/depannage" size="lg" data-testid="home-depannage-cta">
                    Découvrir les prestations <ArrowRight className="h-4 w-4" />
                  </GoldButton>
                </div>
              </div>
              <ul className="space-y-4">
                {[
                  ["Un prix connu à l'avance", "petite fourniture, main-d'œuvre et nettoyage inclus lorsque prévu"],
                  ["Le créneau de votre choix", "jour et heure sélectionnés parmi les disponibilités"],
                  ["Votre livret de dépannages", "chaque intervention conservée avec son compte-rendu"],
                ].map(([t, d]) => (
                  <li key={t} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-navy/60 p-5">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                      <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </span>
                    <div><p className="text-white">{t}</p><p className="text-sm text-slate-400">{d}</p></div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </section>

      {/* UN NOUVEAU RÉFLEXE */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-28 lg:px-8">
        <Reveal><Overline>{cms("home.manifesto.overline", "Un nouveau réflexe")}</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
            {cms("home.manifesto.title", "Le dépannage ne devrait jamais reposer sur une seule personne.")}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            Speed Détresse transforme une chaîne de messages et d'appels en un cahier opérationnel vivant : chaque information arrive au bon interlocuteur, au bon moment.
          </p>
        </Reveal>
        <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {REFLEXE.map((r, i) => (
            <Reveal key={r.n} delay={i * 0.08} className="bg-navy p-8">
              <span className="font-serif text-3xl text-gold md:text-4xl">{r.n}</span>
              <p className="mt-4 text-slate-300">{r.t}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* UNE APPLICATION POUR TOUT LE LOGEMENT */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>La mémoire du logement</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              Une application pour tout le logement.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              Chaque logement dispose de son propre espace numérique : sa mémoire est conservée dans le temps et l'information circule entre occupants, propriétaires et professionnels.
            </p>
          </Reveal>
          <div className="mt-12 flex flex-wrap gap-3" data-testid="home-logement-items">
            {LOGEMENT_ITEMS.map((item, i) => (
              <Reveal key={item} delay={(i % 5) * 0.04}>
                <span className="flex items-center gap-2.5 rounded-full border border-white/10 bg-navy px-4 py-2.5 text-sm text-slate-200">
                  <Check className="h-3.5 w-3.5 text-gold" strokeWidth={2.5} /> {item}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 font-serif text-2xl italic text-gold">Un logement, un suivi clair et indépendant.</p>
          </Reveal>
        </div>
      </section>

      {/* INNOVATION IA */}
      <section className="relative z-10 overflow-hidden py-28">
        <div className="absolute right-0 top-0 h-full w-1/2 opacity-20">
          <img src={IMAGES.aiNodes} alt="" className="h-full w-full object-cover" />
          <div className="hero-overlay-grad absolute inset-0 bg-gradient-to-r from-navy via-navy/60 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>{cms("home.ai.overline", "L'intelligence qui fait gagner du temps")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-2xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              {cms("home.ai.title", "Une intelligence conçue pour vous simplifier la vie.")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-2xl text-slate-300">
              L'IA ne remplace pas l'artisan : elle lui transmet de meilleures informations et réduit les tâches répétitives.
              <span className="text-gold"> L'IA organise et accompagne. Le professionnel intervient et décide.</span>
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {AI_ASSISTANTS.map((a, i) => {
              const Icon = AI_ICONS[a.id];
              return (
                <Reveal key={a.id} delay={i * 0.1}>
                  <div
                    data-testid={`ai-card-${a.id}`}
                    className="group h-full rounded-2xl border border-white/10 bg-navy-surface/70 p-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition-colors hover:border-gold/40"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-serif text-2xl text-white">{a.name}</h3>
                    <p className="mt-1 text-sm uppercase tracking-wide text-gold">{a.role}</p>
                    <p className="mt-4 text-slate-300">{a.tagline}</p>
                    <ul className="mt-5 space-y-2.5">
                      {a.points.map((p) => (
                        <li key={p} className="flex gap-2.5 text-sm text-slate-400">
                          <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-gold" /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl font-serif text-2xl italic text-gold">
              Parlez à l'assistant au lieu de gérer seul toute la chaîne du dépannage.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LE CAHIER EN ACTION */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pb-28 lg:px-8">
        <Reveal><Overline>Le cahier en action</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl tracking-tight text-white md:text-5xl">
            Une alerte. Un seul fil. Une résolution.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-action-steps">
          {ACTION_STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08}>
              <div className="relative h-full rounded-2xl border border-white/10 bg-navy-surface/60 p-7">
                <span className="absolute right-5 top-5 font-serif text-4xl text-gold/25">{i + 1}</span>
                <s.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-4 font-serif text-xl text-white">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-400">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-slate-400">
            Chaque étape est reliée dans un même dossier : l'information ne se perd plus entre le signalement, la décision, la préparation et l'intervention.
          </p>
        </Reveal>
      </section>

      {/* CARNET DE SANTÉ */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <Overline>{cms("home.carnet.overline", "Le Carnet de Santé du Logement")}</Overline>
            <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">
              {cms("home.carnet.title", "Le dossier médical de votre logement.")}
            </h2>
            <p className="mt-5 text-lg text-slate-300">
              {cms("home.carnet.text", "Chaque intervention ou maintenance rejoint l'historique du bien : ce qui a été fait, quand, pourquoi, par qui — et ce qui devra être vérifié ensuite. Le suivi régulier documente l'entretien, prépare une location ou une vente et valorise votre patrimoine. Le logement garde sa mémoire.")}
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {CARNET_ITEMS.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-slate-300">
                  <HeartPulse className="h-4 w-4 flex-shrink-0 text-gold" strokeWidth={1.6} /> {item}
                </li>
              ))}
            </ul>
            <div className="mt-9">
              <GoldButton to="/solutions" variant="outline">Découvrir le Carnet de Santé <ArrowRight className="h-4 w-4" /></GoldButton>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img src={cms("home.carnet.image", IMAGES.carnet)} alt="Carnet de Santé du Logement" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
              <div className="absolute bottom-6 left-6 rounded-2xl border border-white/15 bg-navy/70 px-5 py-4 backdrop-blur-md">
                <p className="font-serif text-3xl text-gold">100%</p>
                <p className="text-xs text-slate-300">de l'historique conservé</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOSTIA INSPECT + MANDATAIRE */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>Les modules qui remplacent le déplacement</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              Le propriétaire n'a pas besoin d'être présent pour que le logement soit suivi.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2" data-testid="home-hostia-mandataire">
            <Reveal>
              <div className="h-full rounded-3xl border border-white/10 bg-navy-surface/60 p-9">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold"><ClipboardCheck className="h-5 w-5" strokeWidth={1.5} /></div>
                <h3 className="mt-5 font-serif text-3xl text-white">Hostia Inspect</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  Les états des lieux et inspections guidés : checklist, photos obligatoires, vidéos, commentaires,
                  détection d'anomalies et comparaison avant/après. Un rapport est généré automatiquement après la visite.
                </p>
                <div className="mt-6"><GoldButton to="/solutions" variant="ghost">Voir l'état des lieux intelligent <ArrowRight className="h-4 w-4" /></GoldButton></div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-white/10 bg-navy-surface/60 p-9">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold"><KeyRound className="h-5 w-5" strokeWidth={1.5} /></div>
                <h3 className="mt-5 font-serif text-3xl text-white">Mandataire Speed Détresse</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  Les opérations que vous ne pouvez pas réaliser vous-même : récupérer ou remettre les clés, effectuer une visite,
                  accueillir un locataire, réaliser un état des lieux, ouvrir le logement à un artisan, contrôler une intervention ou après travaux.
                </p>
                <div className="mt-6"><GoldButton to="/souscription" variant="ghost">En parler avec nous <ArrowRight className="h-4 w-4" /></GoldButton></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ÉTAT DES LIEUX TEASER */}
      <section className="relative z-10 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal className="max-w-2xl">
              <div className="flex items-center gap-3 text-gold"><ScanLine className="h-6 w-6" strokeWidth={1.5} /><Overline>L'état des lieux nouvelle génération</Overline></div>
              <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">
                {cms("home.etat.title", "État des lieux intelligent")}
              </h2>
              <p className="mt-5 text-lg text-slate-300">
                {cms("home.etat.text", "Une check-list adaptée à chaque logement : chaque élément est contrôlé, commenté et photographié. Demandez « ajouter les poignées de porte » et l'élément apparaît dans la liste. Le rapport est signé, archivé et comparé au précédent état des lieux.")}
              </p>
              <div className="mt-8">
                <GoldButton to="/solutions" size="lg">Voir la prestation <ArrowRight className="h-4 w-4" /></GoldButton>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <figure className="overflow-hidden rounded-3xl border border-white/10">
                <img src="/images/story-etat-lieux.webp" alt="Propriétaire et locataire réalisant l'état des lieux sur smartphone" loading="lazy" className="aspect-[4/3] w-full object-cover" data-testid="home-etat-image" />
                <figcaption className="bg-navy-surface/60 p-5">
                  <p className="text-sm text-slate-300"><span className="text-gold">Propriétaire et locataire, ensemble.</span> L'état des lieux se fait sereinement, chaque élément photographié et signé.</p>
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      {/* UNE RÉPONSE ADAPTÉE À CHAQUE ACTEUR */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>{cms("home.acteurs.overline", "Un écosystème connecté")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              {cms("home.acteurs.title", "Une réponse adaptée à chaque acteur.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-acteurs">
            {ACTEURS.map((a, i) => (
              <Reveal key={a.t} delay={(i % 3) * 0.08}>
                <div className="group h-full rounded-2xl border border-white/10 bg-navy p-8 transition-colors hover:border-gold/40">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                    <a.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-white">{a.t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{a.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <section className="relative z-10 border-t border-white/10 mx-auto max-w-7xl px-5 py-28 lg:px-8">
        <Reveal><Overline>{cms("home.personas.overline", "Un écosystème, pas une simple application")}</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">{cms("home.personas.title", "Tout le monde avance dans le même sens.")}</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 max-w-2xl text-slate-300">
            Speed Détresse donne à chaque utilisateur son bon niveau d'accès, ses bons outils et une vision partagée du dossier.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {PERSONAS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <Link
                to="/espaces"
                data-testid={`home-persona-${p.id}`}
                className="group relative block overflow-hidden rounded-3xl border border-white/10"
              >
                <img src={p.image} alt={p.label} className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="on-image absolute inset-x-0 bottom-0 p-7">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold">{p.label}</p>
                  <h3 className="mt-2 font-serif text-2xl text-white md:text-3xl">{p.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-2 text-sm text-slate-200">
                    Explorer <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ILS FONT APPEL À SPEED DÉTRESSE */}
      <section className="relative z-10 border-t border-white/10 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>Ils font appel à Speed Détresse</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              De la détresse à la solution : un clic suffit.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              Une fuite, une panne, un équipement en détresse — où que vous soyez, quelle que soit votre activité, Speed Détresse organise l'intervention.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-publics">
            {PUBLICS.map((p, i) => (
              <Reveal key={p.t} delay={(i % 4) * 0.07}>
                <figure className="group relative overflow-hidden rounded-3xl border border-white/10">
                  <img src={p.img} alt={p.t} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" />
                  <figcaption className="on-image absolute inset-x-0 bottom-0 p-5">
                    <p className="font-serif text-lg text-white">{p.t}</p>
                    <p className="text-xs text-slate-300">{p.d}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* UNE DIFFÉRENCE CONCRÈTE — TABLEAU COMPARATIF */}
      <section className="relative z-10 border-t border-white/10 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>{cms("home.diff.overline", "Une différence concrète")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              {cms("home.diff.title", "Pourquoi choisir Speed Détresse ?")}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-12 overflow-hidden rounded-3xl border border-white/10">
            <div className="overflow-x-auto" data-testid="home-diff-table">
              <table className="w-full min-w-[640px] border-collapse text-left">
                <thead>
                  <tr>
                    <th className="border-b border-white/10 bg-navy-surface/60 p-5 text-xs uppercase tracking-[0.2em] text-slate-400">
                      {cms("home.diff.head1", "Problème courant")}
                    </th>
                    <th className="border-b border-gold/25 bg-navy-elevated p-5 text-xs uppercase tracking-[0.2em] text-gold">
                      {cms("home.diff.head2", "Réponse Speed Détresse")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {DIFF_ROWS.map(([problem, answer], i) => (
                    <tr key={problem}>
                      <td className={`p-5 text-sm text-slate-300 ${i % 2 ? "bg-navy-surface/40" : "bg-navy"}`}>{problem}</td>
                      <td className={`p-5 text-sm text-white ${i % 2 ? "bg-navy-elevated/60" : "bg-navy-elevated/40"}`}>
                        <span className="flex items-center gap-3">
                          <Check className="h-4 w-4 flex-shrink-0 text-gold" strokeWidth={2.5} /> {answer}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE + ENGAGEMENTS */}
      <section className="relative z-10 border-t border-white/10 py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>{cms("home.why.overline", "Pourquoi choisir Speed Détresse")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              {cms("home.why.title", "Une seule solution réunit tout l'essentiel.")}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w} delay={(i % 3) * 0.05}>
                <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-navy-surface/60 p-5">
                  <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                  <span className="text-slate-200">{w}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-12">
            <p className="text-sm uppercase tracking-[0.2em] text-gold">Nos engagements</p>
            <div className="mt-4 flex flex-wrap gap-3" data-testid="home-engagements">
              {ENGAGEMENTS.map((e) => (
                <span key={e} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300">
                  <ShieldCheck className="h-3.5 w-3.5 text-gold" /> {e}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pb-28 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-gold/25 bg-navy-elevated px-8 py-16 text-center md:px-16 md:py-20">
            <div className="absolute inset-0 gold-radial" />
            <div className="relative">
              <h2 className="mx-auto max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
                {cms("home.cta.title", "Arrêtez de gérer vos logements dans l'urgence.")}
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-slate-300">
                {cms("home.cta.text", "Propriétaire, bailleur, gestionnaire, agence, hôte Airbnb, professionnel ou particulier : Speed Détresse transforme chaque demande en intervention organisée.")}
              </p>
              <div className="mx-auto mt-8 grid max-w-2xl gap-2.5 text-left sm:grid-cols-2">
                {CTA_QUESTIONS.map((q) => (
                  <p key={q} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" strokeWidth={2.5} /> {q}
                  </p>
                ))}
              </div>
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                <GoldButton to="/contact" size="lg">Demander une intervention</GoldButton>
                <GoldButton to="/tarifs" size="lg" variant="outline">Souscrire à Speed Détresse</GoldButton>
                <GoldButton to="/rejoindre" size="lg" variant="ghost">Rejoindre le réseau professionnel</GoldButton>
              </div>
              <div className="mx-auto mt-10 max-w-xl">
                <NotifyForm />
              </div>
              <p className="mt-8 font-serif text-lg italic text-gold/90">
                Speed Détresse — Le dépannage, l'entretien et la mémoire de vos logements réunis dans une même solution.
              </p>
              <p className="mt-2 text-sm text-slate-400">
                Un problème signalé. Une intervention organisée. Un logement préservé.
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
